import { useEffect, useRef } from "react";

export default function Pricing() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            el.querySelectorAll("[data-animate]").forEach((child, i) => {
              setTimeout(() => {
                child.classList.add("animate-fade-in-up");
                child.classList.remove("opacity-0");
              }, i * 150);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="pricing"
      ref={sectionRef}
      className="relative py-20 md:py-28 section-padding bg-[#f8f9fb]"
    >
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-emerald-500/[0.03] rounded-full blur-3xl" />

      <div className="container-wide relative z-10">
        <div className="text-center mb-14 md:mb-20 max-w-2xl mx-auto">
          <div className="opacity-0" data-animate>
            <span className="inline-block px-4 py-1.5 bg-emerald-50 border border-emerald-100 rounded-full text-emerald-600 text-xs md:text-sm font-semibold tracking-wide mb-4">
              Pricing
            </span>
          </div>
          <h2
            className="opacity-0 font-outfit font-bold text-3xl md:text-4xl lg:text-5xl text-gray-900 mb-4"
            data-animate
          >
            Translate Cloud API
          </h2>
          <p
            className="opacity-0 text-base md:text-lg text-gray-500 leading-relaxed"
            data-animate
          >
            A simple and transparent license to deploy{" "}
            <strong className="text-gray-700">Translate Cloud API</strong> on
            your site. Support and installation available as options.
          </p>
        </div>

        <div className="max-w-md mx-auto">
          <div
            data-animate
            className="opacity-0 relative bg-white rounded-2xl border border-gray-200 overflow-hidden transition-all duration-500 hover:shadow-xl hover:shadow-emerald-500/5 hover:border-emerald-200"
          >
            <div className="absolute top-0 right-0 bg-emerald-600 text-white text-xs font-bold px-4 py-1.5 rounded-bl-xl">
              POPULAR
            </div>

            <div className="p-8 md:p-10">
              <h3 className="font-outfit font-bold text-xl text-gray-900 mb-2">
                Translate Cloud API
              </h3>
              <p className="text-sm text-gray-500 mb-6">
                Unified SaaS translation solution
              </p>

              <div className="flex items-baseline gap-1 mb-1">
                <span className="font-outfit font-bold text-5xl md:text-6xl text-gray-900">
                  89.99
                </span>
                <span className="font-outfit font-bold text-2xl text-gray-900">
                  $
                </span>
              </div>
              <p className="text-sm font-medium text-gray-500 mb-8">
                License per site
              </p>

              <ul className="space-y-4 mb-8">
                <li className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-emerald-50 flex items-center justify-center flex-shrink-0">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-check-line text-emerald-600 text-sm" />
                    </div>
                  </div>
                  <span className="text-sm text-gray-700">
                    License per site
                  </span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-emerald-50 flex items-center justify-center flex-shrink-0">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-check-line text-emerald-600 text-sm" />
                    </div>
                  </div>
                  <span className="text-sm text-gray-700">
                    Free 30-day support
                  </span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-emerald-50 flex items-center justify-center flex-shrink-0">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-check-line text-emerald-600 text-sm" />
                    </div>
                  </div>
                  <span className="text-sm text-gray-700">
                    Updates included
                  </span>
                </li>
              </ul>

              <div className="border-t border-gray-200 pt-6 mb-8">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 flex items-center justify-center text-gray-400">
                      <i className="ri-tools-line text-sm" />
                    </div>
                    <span className="text-sm text-gray-600">
                      Application installation
                    </span>
                  </div>
                  <span className="font-outfit font-bold text-lg text-gray-900">
                    50 $
                  </span>
                </div>
              </div>

              <a
                href="https://forcekeys.com/"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="block w-full text-center px-8 py-3.5 bg-emerald-600 text-white rounded-full font-semibold text-sm md:text-base hover:bg-emerald-700 transition-all duration-300 whitespace-nowrap"
              >
                Get Started
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}